import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaSortUp, FaSortDown } from 'react-icons/fa';
import Layout from '../components/Layout';
import { getNotesByYear } from '../data/notesData';

function Notes() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [sortDirection, setSortDirection] = useState('desc');

    const notes = getNotesByYear();

    // Get all years and count of entries
    const years = useMemo(() => {
        return notes.map(yearGroup => ({
            year: yearGroup.year,
            count: yearGroup.entries.length
        }));
    }, [notes]);

    // Get all unique tags
    const allTags = useMemo(() => {
        return Array.from(
            new Set(
                notes.flatMap(yearGroup =>
                    yearGroup.entries.flatMap(note => note.tags)
                )
            )
        ).sort();
    }, [notes]);

    // Filter and sort notes
    const filteredNotes = useMemo(() => {
        // Filter by year first
        let filtered = selectedYear
            ? notes.filter(group => group.year === selectedYear)
            : notes;

        // Filter by search term and tags
        filtered = filtered.map(yearGroup => ({
            year: yearGroup.year,
            entries: yearGroup.entries.filter(note => {
                const matchesSearch =
                    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    note.description.toLowerCase().includes(searchTerm.toLowerCase());

                const matchesTags =
                    selectedTags.length === 0 ||
                    selectedTags.every(tag => note.tags.includes(tag));

                return matchesSearch && matchesTags;
            })
        })).filter(group => group.entries.length > 0);

        // Sort entries within each year group
        return filtered.map(yearGroup => ({
            ...yearGroup,
            entries: [...yearGroup.entries].sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
            })
        }));
    }, [notes, searchTerm, selectedTags, sortDirection, selectedYear]);

    const toggleTag = (tag) => {
        setSelectedTags(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };

    const toggleSort = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    return (
        <Layout maxWidth="max-w-7xl" bg="bg-primary-light dark:bg-primary-dark">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-12">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Notes</h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300">
                        Thoughts, tutorials, and discoveries about technology, research, and life.
                    </p>
                </div>

                <div className="flex gap-8">
                    {/* Left Sidebar - Timeline */}
                    <div className="w-48 shrink-0">
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Timeline</h2>
                        <div className="space-y-2">
                            <button
                                onClick={() => setSelectedYear(null)}
                                className={`w-full text-left px-3 py-2 rounded ${selectedYear === null
                                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                All Years
                            </button>
                            {years.map(({ year, count }) => (
                                <button
                                    key={year}
                                    onClick={() => setSelectedYear(year)}
                                    className={`w-full text-left px-3 py-2 rounded flex justify-between items-center ${selectedYear === year
                                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                        }`}
                                >
                                    <span>{year}</span>
                                    <span className="text-sm">{count}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                        {/* Search and Sort */}
                        <div className="mb-8 flex gap-4">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    placeholder="Search notes..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-4 py-3 pl-12 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-gray-400"
                                />
                                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            </div>
                            <button
                                onClick={toggleSort}
                                className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                            >
                                {sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />}
                                <span>Date</span>
                            </button>
                        </div>

                        {/* Notes List */}
                        <div className="space-y-12">
                            {filteredNotes.map((yearGroup) => (
                                <section key={yearGroup.year}>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                                        {yearGroup.year}
                                    </h2>
                                    <div className="space-y-8">
                                        {yearGroup.entries.map((note) => (
                                            <article key={note.slug} className="group">
                                                <Link to={`/notes/${note.slug}`}>
                                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-orange-500 dark:group-hover:text-orange-400 mb-2">
                                                        {note.title}
                                                    </h3>
                                                </Link>
                                                <time className="text-sm text-gray-500 dark:text-gray-400">
                                                    {new Date(note.date).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </time>
                                                <p className="mt-3 text-gray-600 dark:text-gray-300">
                                                    {note.description}
                                                </p>
                                                <div className="mt-4 flex flex-wrap gap-2">
                                                    {note.tags.map((tag) => (
                                                        <span
                                                            key={tag}
                                                            className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>

                    {/* Right Sidebar - Tags */}
                    <div className="w-56 shrink-0">
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Filter by Tags</h2>
                        <div className="space-y-2">
                            {allTags.map((tag) => (
                                <button
                                    key={tag}
                                    onClick={() => toggleTag(tag)}
                                    className={`w-full text-left px-3 py-2 rounded ${selectedTags.includes(tag)
                                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                        }`}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Notes;
